import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import {
  GLTFLoader,
  DRACOLoader,
  RGBELoader,
  RoomEnvironment,
} from "three/examples/jsm/Addons.js";
import GUI from "lil-gui";

/**
 * Base
 */
const gui = new GUI();
if (!import.meta.env.DEV) gui.hide();
const canvas = document.querySelector("canvas.webgl");
const scene = new THREE.Scene();
scene.background = new THREE.Color("#D22B2B");

/**
 * Loaders
 */
const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath("/draco/");
const gltfLoader = new GLTFLoader();
gltfLoader.setDRACOLoader(dracoLoader);

const textureLoader = new THREE.TextureLoader();

let roomModel = null;

/**
 * Loading Screen
*/
const loadingScreenEl = document.querySelector(".loading-screen");
const tourStopsEl = document.querySelector(".tour-stops");
function hideLoadingScreen() {
  loadingScreenEl.classList.add("is-hidden");
  loadingScreenEl.setAttribute("aria-hidden", "true");
  tourStopsEl.classList.add("is-visible");
}

/**
 * Door
 */
const DOOR_NODE_NAME = "Door";
let doorGroup = null;
let doorBasePosition = null;
const doorConfig = {
  axis: "z",
  distance: 2, // meters the door travels, fully open at Stop II
};

/**
 * Screen Video Texture
 */
let screenVideo = null;
let screenCanvas = null;
let screenCanvasCtx = null;
let screenCanvasTexture = null;
let screenMesh = null;

const SCREEN_YOUTUBE_URL = "https://www.youtube.com/watch?v=b7kA9OYIg6Q";

/**
 * Shows Poster
 */
const POSTER_NODE_NAME = "YSSC";
let posterMesh = null;

const SCREEN_CANVAS_WIDTH = 1280;
const SCREEN_CANVAS_HEIGHT = 720;

const screenVideoState = {
  rotationDeg: 90,
  scale: 0.25,
  offsetX: 0,
  offsetY: 270,
  flipX: true,
  flipY: false,
};

gltfLoader.load(
  "/models/Room.gltf",
  (gltf) => {
    console.log("Room loaded");
    roomModel = gltf.scene;
    scene.add(roomModel);

    const maxAnisotropy = renderer.capabilities.getMaxAnisotropy();

    doorGroup = roomModel.getObjectByName(DOOR_NODE_NAME);
    if (doorGroup) {
      doorBasePosition = doorGroup.position.clone();
    } else {
      console.warn(
        `No node named "${DOOR_NODE_NAME}" found in Room.gltf — the door (and its stickers) won't slide. Check the exact name in Blender.`,
      );
    }

    posterMesh = roomModel.getObjectByName(POSTER_NODE_NAME);
    if (!posterMesh) {
      console.warn(`No node named "${POSTER_NODE_NAME}" found in Room.gltf — the Shows poster won't be clickable.`);
    }

    roomModel.traverse((child) => {
      if (!child.isMesh) return;

      const worldPos = new THREE.Vector3();
      child.getWorldPosition(worldPos);
      console.log(child.name, worldPos, "material:", child.material?.name);

      child.castShadow = true;
      child.receiveShadow = true;

      const materials = Array.isArray(child.material)
        ? child.material
        : [child.material];

      materials.forEach((material) => {
        if (!material) return;

        if (material.map) material.map.colorSpace = THREE.SRGBColorSpace;
        if (material.emissiveMap)
          material.emissiveMap.colorSpace = THREE.SRGBColorSpace;
        if (material.normalMap)
          material.normalMap.colorSpace = THREE.NoColorSpace;
        if (material.roughnessMap)
          material.roughnessMap.colorSpace = THREE.NoColorSpace;
        if (material.metalnessMap)
          material.metalnessMap.colorSpace = THREE.NoColorSpace;
        if (material.aoMap) material.aoMap.colorSpace = THREE.NoColorSpace;

        Object.values(material)
          .filter((value) => value instanceof THREE.Texture)
          .forEach((texture) => {
            texture.anisotropy = maxAnisotropy;
          });

        material.needsUpdate = true;
      });
    });

    measureDoorScrollRange();
    measureStopScrollOffsets();
    hideLoadingScreen();

    // Deferred until after the room is visible: the screen video (13MB)
    // would otherwise start downloading immediately and compete for
    // bandwidth with the model/textures on the critical load path,
    // especially noticeable on mobile connections.
    applyExternalTextures();
  },
  () => {},
  (error) => {
    console.error("Error loading Room.gltf", error);
    hideLoadingScreen();
  },
);

const applyExternalTextures = () => {
  if (!roomModel) return;
  screenMesh = roomModel.getObjectByName("Screen");
  if (screenMesh) {
    setupScreenVideo(screenMesh);
  } else {
    console.warn('No mesh named "Screen" found in Room.gltf — check the exact name in Blender.');
  }
};

function setupScreenVideo(mesh) {
  screenVideo = document.createElement("video");
  screenVideo.src = "/videos/dripkidPromo.mp4";
  screenVideo.loop = true;
  screenVideo.muted = true; // required for autoplay in every major browser
  screenVideo.playsInline = true;
  screenVideo.autoplay = true;
  screenVideo.crossOrigin = "anonymous";

  screenVideo.addEventListener("error", () => {
    console.error(
      `Screen video failed to load from "${screenVideo.src}". ` +
        "If devtools shows it decoding as text/html, the file isn't at that path — " +
        "your dev server is likely returning its SPA fallback page instead of the video. " +
        "Confirm the file actually sits in your static/public assets folder.",
    );
  });

  screenVideo.play().catch(() => {
    const resume = () => {
      screenVideo.play();
      window.removeEventListener("pointerdown", resume);
    };
    window.addEventListener("pointerdown", resume, { once: true });
  });

  screenCanvas = document.createElement("canvas");
  screenCanvas.width = SCREEN_CANVAS_WIDTH;
  screenCanvas.height = SCREEN_CANVAS_HEIGHT;
  screenCanvasCtx = screenCanvas.getContext("2d");
  screenCanvasTexture = new THREE.CanvasTexture(screenCanvas);
  screenCanvasTexture.colorSpace = THREE.SRGBColorSpace;
  screenCanvasTexture.minFilter = THREE.LinearFilter;
  screenCanvasTexture.magFilter = THREE.LinearFilter;
  screenCanvasTexture.generateMipmaps = false;

  mesh.material = new THREE.MeshBasicMaterial({
    map: screenCanvasTexture,
    transparent: true,
  });

  if (screenVideo.requestVideoFrameCallback) {
    const drawOnNewFrame = () => {
      drawScreenVideoFrame();
      screenVideo.requestVideoFrameCallback(drawOnNewFrame);
    };
    screenVideo.requestVideoFrameCallback(drawOnNewFrame);
  }
}

function drawScreenVideoFrame() {
  if (!screenVideo || !screenCanvasCtx || screenVideo.readyState < 2) return;

  const { rotationDeg, scale, offsetX, offsetY, flipX, flipY } = screenVideoState;
  const rotated = rotationDeg % 180 !== 0;
  const videoW = screenVideo.videoWidth;
  const videoH = screenVideo.videoHeight;
  if (!videoW || !videoH) return;

  const videoAspect = videoW / videoH;

  const drawHeight = SCREEN_CANVAS_HEIGHT * scale;
  const drawWidth = drawHeight * videoAspect;

  screenCanvasCtx.save();
  screenCanvasCtx.clearRect(0, 0, SCREEN_CANVAS_WIDTH, SCREEN_CANVAS_HEIGHT);

  screenCanvasCtx.translate(SCREEN_CANVAS_WIDTH / 2 + offsetX, SCREEN_CANVAS_HEIGHT / 2 + offsetY);
  screenCanvasCtx.rotate((rotationDeg * Math.PI) / 180);
  screenCanvasCtx.scale(flipX ? -1 : 1, flipY ? -1 : 1);

  const nativeDrawWidth = rotated ? drawHeight : drawWidth;
  const nativeDrawHeight = rotated ? drawWidth : drawHeight;
  screenCanvasCtx.drawImage(
    screenVideo,
    -nativeDrawWidth / 2,
    -nativeDrawHeight / 2,
    nativeDrawWidth,
    nativeDrawHeight,
  );
  screenCanvasCtx.restore();

  screenCanvasTexture.needsUpdate = true;
}

/**
 * Floor
*/
const floor = new THREE.Mesh(
  new THREE.PlaneGeometry(10, 10),
  new THREE.ShadowMaterial({ opacity: 0.35 }),
);
floor.receiveShadow = true;
floor.rotation.x = -Math.PI * 0.5;
scene.add(floor);

/**
 * Lights
 */
const directionalLight = new THREE.DirectionalLight(0xffffff, 3);
directionalLight.castShadow = true;
directionalLight.shadow.mapSize.set(2048, 2048);
directionalLight.shadow.camera.far = 15;
directionalLight.shadow.camera.left = -7;
directionalLight.shadow.camera.top = 7;
directionalLight.shadow.camera.right = 7;
directionalLight.shadow.camera.bottom = -7;
directionalLight.shadow.bias = -0.001;
directionalLight.shadow.normalBias = 0.02;
directionalLight.position.set(5, 5, 5);
scene.add(directionalLight);

const fillLight = new THREE.HemisphereLight(0xfff4e8, 0x241d17, 0.6);
scene.add(fillLight);

const rimLight = new THREE.DirectionalLight(0xffe3c2, 1.1);
rimLight.position.set(-4, 3, -5);
scene.add(rimLight);

const lightingFolder = gui.addFolder("Lighting");
lightingFolder.add(directionalLight, "intensity", 0, 10, 0.1).name("Key intensity");
lightingFolder
  .addColor({ color: directionalLight.color.getHex() }, "color")
  .name("Key color")
  .onChange((hex) => directionalLight.color.set(hex));
lightingFolder.add(directionalLight.position, "x", -10, 10, 0.1).name("Key X");
lightingFolder.add(directionalLight.position, "y", -10, 10, 0.1).name("Key Y");
lightingFolder.add(directionalLight.position, "z", -10, 10, 0.1).name("Key Z");
lightingFolder.add(fillLight, "intensity", 0, 5, 0.05).name("Fill intensity");
lightingFolder.add(rimLight, "intensity", 0, 5, 0.05).name("Rim intensity");
lightingFolder
  .addColor({ color: rimLight.color.getHex() }, "color")
  .name("Rim color")
  .onChange((hex) => rimLight.color.set(hex));
lightingFolder
  .add({ envIntensity: 1 }, "envIntensity", 0, 5, 0.05)
  .name("Environment intensity")
  .onChange((value) => {
    scene.traverse((child) => {
      if (child.isMesh && child.material) {
        const materials = Array.isArray(child.material) ? child.material : [child.material];
        materials.forEach((m) => {
          if ("envMapIntensity" in m) m.envMapIntensity = value;
        });
      }
    });
  });

/**
 * Sizes
 */
const sizes = { width: window.innerWidth, height: window.innerHeight };

window.addEventListener("resize", () => {
  sizes.width = window.innerWidth;
  sizes.height = window.innerHeight;
  camera.aspect = sizes.width / sizes.height;
  camera.updateProjectionMatrix();
  renderer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  measureDoorScrollRange();
  measureStopScrollOffsets();
});

/**
 * Camera
 */
const camera = new THREE.PerspectiveCamera(20, sizes.width / sizes.height, 0.1, 100);
scene.add(camera);

const controls = new OrbitControls(camera, canvas);
controls.enableDamping = true;
controls.enabled = false;
// OrbitControls sets touch-action: none on its element unconditionally
// (regardless of `enabled`), which blocks native swipe-to-scroll on
// touch devices since the canvas covers the full viewport. Restore
// vertical panning while orbit controls aren't in use.
controls.domElement.style.touchAction = "pan-y";

/**
 * Stops
 */
const STOPS = [
  {
    kind: "hero",
    title: "ORACLE",
    position: new THREE.Vector3(31.85, 16.75, 31.55),
    lookAt: new THREE.Vector3(-0.35, 4.33, -0.46),
    fov: 20,
  },
  {
    kind: "content",
    title: "Links",
    position: new THREE.Vector3(-2.09, 3.71, -0.36), 
    lookAt: new THREE.Vector3(-2.32, 3.75, -0.11),
    fov: 60,
    expand: {
      type: "links",
      heading: "Links",
      links: [
        { title: "Dripkid", href: "https://open.spotify.com/album/7mXlw7bMEiG6j4NR1mJ2a4", icon: "dripkid" },
        { title: "Spotify", href: "https://open.spotify.com/artist/5ngejTpnC2HYvbgLwXSmNc", icon: "spotify" },
        { title: "Insta", href: "https://www.instagram.com/oraclethepoet", icon: "instagram" },
        { title: "Tiktok", href: "https://www.tiktok.com/@thedrippiestkid", icon: "tiktok" },
      ],
    },
  },
  {
    kind: "silent",
    title: "Computer",
    position: new THREE.Vector3(1.58, 5.32, 1.96), 
    lookAt: new THREE.Vector3(1.62, 4.09, -2.68),
    fov: 40,
  },
  {
    kind: "silent",
    title: "Shows",
    position: new THREE.Vector3(-1.27, 5.01, 0.79),
    lookAt: new THREE.Vector3(-1.29, 4.07, -2.65),
    fov: 40,
    expand: {
      type: "links",
      heading: "Shows",
      links: [],
    },
  },
];

const tourStopsRoot = document.querySelector(".tour-stops");
const stopRailRoot = document.querySelector(".stop-rail");
const stopSections = [];
const railButtons = []; // parallel to stopSections; null where there's no dot

STOPS.forEach((stop, index) => {
  const section = document.createElement("section");
  section.className = `stop stop--${stop.kind}`;
  section.dataset.index = String(index);

  if (stop.kind === "hero") {
    section.innerHTML = `
      <div class="stop__content">
        <img class="stop__title stop__logo" src="/logo/Logo.webp" alt="${stop.title}" />
      </div>
    `;
  } else if (stop.kind === "content") {
    section.innerHTML = `
      <div class="stop__content">
        <button class="stop__title-button" type="button">${stop.title}</button>
        ${stop.teaser ? `<p class="stop__teaser">${stop.teaser}</p>` : ""}
      </div>
    `;
    section.querySelector(".stop__title-button").addEventListener("click", () => openOverlay(index));
  }

  tourStopsRoot.appendChild(section);
  stopSections.push(section);

  if (stop.kind === "flythrough") {
    railButtons.push(null);
  } else {
    const railButton = document.createElement("button");
    railButton.className = "stop-rail__item";
    railButton.type = "button";
    railButton.setAttribute("aria-label", `Go to ${stop.title}`);
    railButton.addEventListener("click", () => section.scrollIntoView({ behavior: "smooth" }));
    stopRailRoot.appendChild(railButton);
    railButtons.push(railButton);
  }
});

let doorScrollStart = 0;
let doorScrollEnd = 0;

function measureDoorScrollRange() {
  if (stopSections.length < 2) return;
  doorScrollStart = stopSections[0].offsetTop;
  doorScrollEnd = stopSections[1].offsetTop;
}

measureDoorScrollRange();

function updateDoorSlide() {
  if (!doorGroup || !doorBasePosition) return;

  const range = doorScrollEnd - doorScrollStart;
  const progress =
    range > 0
      ? THREE.MathUtils.clamp((window.scrollY - doorScrollStart) / range, 0, 1)
      : 0;

  const offset = doorConfig.distance * progress;
  doorGroup.position[doorConfig.axis] = doorBasePosition[doorConfig.axis] + offset;
}

const doorFolder = gui.addFolder("Door calibration");
doorFolder.add(doorConfig, "axis", ["x", "z"]).name("Slide axis");
doorFolder.add(doorConfig, "distance", 0, 3, 0.01).name("Slide distance");

const screenVideoFolder = gui.addFolder("Screen video");
screenVideoFolder
  .add(screenVideoState, "rotationDeg", -180, 180, 1)
  .name("Rotation (deg)")
  .onChange(() => drawScreenVideoFrame());
screenVideoFolder
  .add(screenVideoState, "scale", 0.1, 3, 0.01)
  .name("Scale")
  .onChange(() => drawScreenVideoFrame());
screenVideoFolder
  .add(screenVideoState, "offsetX", -SCREEN_CANVAS_WIDTH / 2, SCREEN_CANVAS_WIDTH / 2, 1)
  .name("Offset X (px)")
  .onChange(() => drawScreenVideoFrame());
screenVideoFolder
  .add(screenVideoState, "offsetY", -SCREEN_CANVAS_HEIGHT / 2, SCREEN_CANVAS_HEIGHT / 2, 1)
  .name("Offset Y (px)")
  .onChange(() => drawScreenVideoFrame());
screenVideoFolder
  .add(screenVideoState, "flipX")
  .name("Flip horizontal")
  .onChange(() => drawScreenVideoFrame());
screenVideoFolder
  .add(screenVideoState, "flipY")
  .name("Flip vertical")
  .onChange(() => drawScreenVideoFrame());
screenVideoFolder
  .add(
    {
      logValues: () => {
        console.log(
          `screenVideoState = { rotationDeg: ${screenVideoState.rotationDeg}, ` +
            `scale: ${screenVideoState.scale.toFixed(2)}, ` +
            `offsetX: ${screenVideoState.offsetX.toFixed(0)}, ` +
            `offsetY: ${screenVideoState.offsetY.toFixed(0)}, ` +
            `flipX: ${screenVideoState.flipX}, ` +
            `flipY: ${screenVideoState.flipY} };`,
        );
      },
    },
    "logValues",
  )
  .name("Log values (console)");

let activeIndex = 0;

const stopObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      setActiveStop(Number(entry.target.dataset.index));
    });
  },
  { threshold: 0.6 },
);

stopSections.forEach((section) => stopObserver.observe(section));

function setActiveStop(index) {
  activeIndex = index;
  stopSections.forEach((section, i) => section.classList.toggle("is-active", i === index));
  railButtons.forEach((button, i) => button?.classList.toggle("is-active", i === index));
}

setActiveStop(0);

const LINK_ICONS = {
  spotify:
    '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>',
  instagram:
    '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>',
  tiktok:
    '<svg fill="#D22B2B" viewBox="0 0 32 32" version="1.1" xmlns="http://www.w3.org/2000/svg" stroke="#D22B2B"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <title>tiktok</title> <path d="M16.656 1.029c1.637-0.025 3.262-0.012 4.886-0.025 0.054 2.031 0.878 3.859 2.189 5.213l-0.002-0.002c1.411 1.271 3.247 2.095 5.271 2.235l0.028 0.002v5.036c-1.912-0.048-3.71-0.489-5.331-1.247l0.082 0.034c-0.784-0.377-1.447-0.764-2.077-1.196l0.052 0.034c-0.012 3.649 0.012 7.298-0.025 10.934-0.103 1.853-0.719 3.543-1.707 4.954l0.020-0.031c-1.652 2.366-4.328 3.919-7.371 4.011l-0.014 0c-0.123 0.006-0.268 0.009-0.414 0.009-1.73 0-3.347-0.482-4.725-1.319l0.040 0.023c-2.508-1.509-4.238-4.091-4.558-7.094l-0.004-0.041c-0.025-0.625-0.037-1.25-0.012-1.862 0.49-4.779 4.494-8.476 9.361-8.476 0.547 0 1.083 0.047 1.604 0.136l-0.056-0.008c0.025 1.849-0.050 3.699-0.050 5.548-0.423-0.153-0.911-0.242-1.42-0.242-1.868 0-3.457 1.194-4.045 2.861l-0.009 0.030c-0.133 0.427-0.21 0.918-0.21 1.426 0 0.206 0.013 0.41 0.037 0.61l-0.002-0.024c0.332 2.046 2.086 3.59 4.201 3.59 0.061 0 0.121-0.001 0.181-0.004l-0.009 0c1.463-0.044 2.733-0.831 3.451-1.994l0.010-0.018c0.267-0.372 0.45-0.822 0.511-1.311l0.001-0.014c0.125-2.237 0.075-4.461 0.087-6.698 0.012-5.036-0.012-10.060 0.025-15.083z"></path> </g></svg>',
  dripkid:
    '<img src="/links/Dripkid.webp" />'
  };

const overlayEl = document.querySelector(".stop-overlay");
const overlayInner = overlayEl.querySelector(".stop-overlay__inner");
const overlayCloseButton = overlayEl.querySelector(".stop-overlay__close");

let overlayOpen = false;

function openOverlay(index) {
  const { expand } = STOPS[index];
  overlayInner.innerHTML = "";

  const heading = document.createElement("h2");
  heading.className = "stop-overlay__heading";
  heading.textContent = expand.heading;
  overlayInner.appendChild(heading);

  if (expand.type === "grid") {
    const grid = document.createElement("div");
    grid.className = "video-grid";
    expand.videos.forEach((video) => {
      const item = document.createElement("div");
      item.className = "video-grid__item";
      item.innerHTML = `
        <video controls preload="none"${video.poster ? ` poster="${video.poster}"` : ""}>
          <source src="${video.src}" />
        </video>
        <p>${video.title}</p>
      `;
      grid.appendChild(item);
    });
    overlayInner.appendChild(grid);
  } else {
    const list = document.createElement("ul");
    list.className = "stop-overlay__links";
    expand.links.forEach((link) => {
      const li = document.createElement("li");
      const a = document.createElement("a");
      a.href = link.href;
      const iconMarkup = link.cover
        ? `<img class="stop-overlay__link-icon stop-overlay__link-cover" src="${link.cover}" alt="" />`
        : link.icon && LINK_ICONS[link.icon]
          ? `<span class="stop-overlay__link-icon">${LINK_ICONS[link.icon]}</span>`
          : "";
      a.innerHTML = `${iconMarkup}<span class="stop-overlay__link-label">${link.title}</span>`;
      if (link.href.startsWith("http")) a.target = "_blank";
      li.appendChild(a);
      list.appendChild(li);
    });
    overlayInner.appendChild(list);
  }

  overlayEl.classList.add("is-open");
  overlayEl.setAttribute("aria-hidden", "false");
  document.body.style.overflow = "hidden";
  overlayOpen = true;
}

function closeOverlay() {
  overlayEl.classList.remove("is-open");
  overlayEl.setAttribute("aria-hidden", "true");
  document.body.style.overflow = "";
  overlayOpen = false;
}

overlayCloseButton.addEventListener("click", closeOverlay);
window.addEventListener("keydown", (event) => {
  if (event.key === "Escape" && overlayOpen) closeOverlay();
});

const screenRaycaster = new THREE.Raycaster();
const pointerNDC = new THREE.Vector2();

function ndcFromEvent(event) {
  const rect = canvas.getBoundingClientRect();
  pointerNDC.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
  pointerNDC.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
}

function isPointerOverMesh(event, mesh) {
  if (!mesh || debugState.editMode) return false;
  ndcFromEvent(event);
  screenRaycaster.setFromCamera(pointerNDC, camera);
  return screenRaycaster.intersectObject(mesh, false).length > 0;
}

const SHOWS_STOP_INDEX = STOPS.findIndex((stop) => stop.title === "Shows");

canvas.addEventListener("pointermove", (event) => {
  const overClickable = isPointerOverMesh(event, screenMesh) || isPointerOverMesh(event, posterMesh);
  canvas.style.cursor = overClickable ? "pointer" : "";
});

canvas.addEventListener("click", (event) => {
  if (isPointerOverMesh(event, screenMesh)) {
    window.open(SCREEN_YOUTUBE_URL, "_blank", "noopener");
  } else if (isPointerOverMesh(event, posterMesh) && SHOWS_STOP_INDEX !== -1) {
    openOverlay(SHOWS_STOP_INDEX);
  }
});

const debugState = {
  editMode: false,
  fov: camera.fov,
  jumpTo: 0,
  logKeyframe: () => {
    const p = camera.position;
    const t = controls.target;
    console.log(
      `{ position: new THREE.Vector3(${p.x.toFixed(2)}, ${p.y.toFixed(2)}, ${p.z.toFixed(2)}), ` +
        `lookAt: new THREE.Vector3(${t.x.toFixed(2)}, ${t.y.toFixed(2)}, ${t.z.toFixed(2)}), ` +
        `fov: ${camera.fov.toFixed(1)} },`,
    );
  },
};

const cameraFolder = gui.addFolder("Camera calibration");
cameraFolder
  .add(debugState, "editMode")
  .name("Edit Camera")
  .onChange((value) => {
    controls.enabled = value;
    controls.domElement.style.touchAction = value ? "none" : "pan-y";
    document.body.classList.toggle("camera-edit-mode", value);
    if (value) {
      if (overlayOpen) closeOverlay();
      debugState.fov = camera.fov;
      fovController.updateDisplay();
    }
  });
const fovController = cameraFolder
  .add(debugState, "fov", 5, 90, 0.5)
  .name("FOV")
  .onChange((value) => {
    camera.fov = value;
    camera.updateProjectionMatrix();
  });
cameraFolder
  .add(debugState, "jumpTo", {
    "I — Oracle": 0,
    "II — Links": 1,
    "III — Computer": 2,
    "IV — Shows": 3,
  })
  .name("Preview stop")
  .onChange((index) => stopSections[index].scrollIntoView({ behavior: "smooth" }));
cameraFolder.add(debugState, "logKeyframe").name("Log Keyframe (console)");
cameraFolder.open();

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true });
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1;

/**
 * Environment
 */
const pmremGenerator = new THREE.PMREMGenerator(renderer);
scene.environment = pmremGenerator.fromScene(new RoomEnvironment(), 0.04).texture;
pmremGenerator.dispose();


const rendererFolder = gui.addFolder("Renderer");
rendererFolder.add(renderer, "toneMappingExposure", 0, 3, 0.01).name("Exposure");

/**
 * Camera
 */
let stopScrollOffsets = [];

function measureStopScrollOffsets() {
  stopScrollOffsets = stopSections.map((section) => section.offsetTop);
}

measureStopScrollOffsets();

// Ken Perlin's smootherstep — zero first *and* second derivative at
// both ends, so consecutive stop-to-stop segments hand off to each
// other with no visible kink in velocity, not just in position.
function smootherstep(t) {
  const clamped = THREE.MathUtils.clamp(t, 0, 1);
  return clamped * clamped * clamped * (clamped * (clamped * 6 - 15) + 10);
}

const scrollTargetPosition = STOPS[0].position.clone();
const scrollTargetLookAt = STOPS[0].lookAt.clone();
let scrollTargetFov = STOPS[0].fov;

function updateScrollTarget() {
  if (stopScrollOffsets.length < 2) {
    scrollTargetPosition.copy(STOPS[activeIndex].position);
    scrollTargetLookAt.copy(STOPS[activeIndex].lookAt);
    scrollTargetFov = STOPS[activeIndex].fov;
    return;
  }

  const scrollY = window.scrollY;

  let i = 0;
  while (i < stopScrollOffsets.length - 2 && scrollY >= stopScrollOffsets[i + 1]) {
    i += 1;
  }

  const segmentStart = stopScrollOffsets[i];
  const segmentEnd = stopScrollOffsets[i + 1];
  const segmentRange = segmentEnd - segmentStart;
  const rawT = segmentRange > 0 ? (scrollY - segmentStart) / segmentRange : 0;
  const t = smootherstep(rawT);

  const from = STOPS[i];
  const to = STOPS[i + 1];

  scrollTargetPosition.copy(from.position).lerp(to.position, t);
  scrollTargetLookAt.copy(from.lookAt).lerp(to.lookAt, t);
  scrollTargetFov = THREE.MathUtils.lerp(from.fov, to.fov, t);
}

const currentPosition = STOPS[0].position.clone();
const currentLookAt = STOPS[0].lookAt.clone();
let currentFov = STOPS[0].fov;

const CAMERA_FOLLOW_EASE = 0.18;

const updateCameraTowardActiveStop = () => {
  updateScrollTarget();

  currentPosition.lerp(scrollTargetPosition, CAMERA_FOLLOW_EASE);
  currentLookAt.lerp(scrollTargetLookAt, CAMERA_FOLLOW_EASE);
  currentFov = THREE.MathUtils.lerp(currentFov, scrollTargetFov, CAMERA_FOLLOW_EASE);

  camera.position.copy(currentPosition);
  camera.fov = currentFov;
  camera.updateProjectionMatrix();
  camera.lookAt(currentLookAt);
  controls.target.copy(currentLookAt);
};

/**
 * Animate
 */
const clock = new THREE.Clock();

const tick = () => {
  if (!debugState.editMode) {
    updateCameraTowardActiveStop();
  }

  updateDoorSlide();

  // Fallback redraw for browsers without requestVideoFrameCallback
  // (e.g. Safari) — setupScreenVideo() already wires up the
  // callback-driven path for everyone else.
  if (screenVideo && !screenVideo.requestVideoFrameCallback) {
    drawScreenVideoFrame();
  }

  controls.update();

  if (!overlayOpen) {
    renderer.render(scene, camera);
  }

  window.requestAnimationFrame(tick);
};

tick();
